import CloseSmallIcon from 'assets/icons/close-small-icon.svg';
import { fetchReceiversCountries } from 'components/Receivers/func';
import DropdownCommon, {
  DropdownItemStyled,
  DropdownMenuContainer,
  DropdownSearchInput,
} from 'components/common/DropdownCommon';
import Endpoint from 'components/common/Endpoint';
import ArrowDownIcon from 'components/common/Icons/ArrowDownIcon';
import { DESKTOP_MIN_WIDTH } from 'constants';
import { searchTextValid } from 'helpers';
import { Fade } from 'helpers/animation';
import useAuth from 'hooks/useAuth';
import useDetectEndpointVisibleOnScreenWithId from 'hooks/useDetectEndpointVisibleOnScreenWithId';
import useInputFocus from 'hooks/useInputFocus';
import useLang from 'hooks/useLang';
import { isEmpty, isNull } from 'lodash';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { arrayUtils } from 'utils';
import FormInputCheckbox from '../FormInputCheckbox';

const FILTER_COUNTRY_SEARCH_NAME = 'receivers-filter-country-search';
const ENDPOINT_ID = 'CustomFilterCountryDropdown-endpoint';

const CustomFilterCountryDropdown = ({ filtered, setFiltered, currentTab }) => {
  const { t } = useLang();
  const { token } = useAuth();

  const [countries, setCountries] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [countriesSearch, setCountriesSearch] = useState(null);
  const [isShowDropdown, setShowDropdown] = useState(false);

  const isSearchInvalid = !searchTextValid(searchValue);

  const { countriesSelected } = filtered;

  const isIntersecting = useDetectEndpointVisibleOnScreenWithId(ENDPOINT_ID);
  const isFetchReceiversCountriesInititial =
    isIntersecting && isNull(countries);
  useEffect(() => {
    if (isFetchReceiversCountriesInititial) {
      const isReceiverOfBusinessType = currentTab === 1;
      fetchReceiversCountries(token, setCountries, isReceiverOfBusinessType);
    }
  }, [isFetchReceiversCountriesInititial, currentTab]);

  useInputFocus(FILTER_COUNTRY_SEARCH_NAME);

  const handleSearch = (e) => {
    const { value } = e.target;

    setSearchValue(value);

    if (!value) {
      setCountriesSearch(countries);

      return;
    }

    const newCountriesSearch = countries?.filter(
      (country) =>
        country?.name?.toLowerCase()?.includes(value?.toLowerCase()) && country
    );
    setCountriesSearch(newCountriesSearch);
  };

  const handleCountrySelected = (e, country) => {
    e.preventDefault();
    e.stopPropagation();

    let newCountriesSelected = countriesSelected ? [...countriesSelected] : [];

    if (
      newCountriesSelected?.some(
        (countrySelected) => countrySelected.code === country.code
      )
    ) {
      newCountriesSelected = countriesSelected?.filter(
        (countrySelected) => countrySelected.code !== country.code
      );
    } else {
      newCountriesSelected.push(country);
    }

    const newFiltered = {
      ...filtered,
      countriesSelected: newCountriesSelected,
    };
    setFiltered(newFiltered);

    setSearchValue('');

    setCountriesSearch(null);
  };

  const handleDropdownOnToggle = () => {
    setSearchValue('');
    setCountriesSearch(countries);
    setShowDropdown(!isShowDropdown);
  };

  const handleDeleteCountrySelected = (e, country) => {
    e.preventDefault();
    e.stopPropagation();

    const newCountriesSelected = countriesSelected?.filter(
      (countrySelected) => countrySelected.code !== country.code
    );

    const newFiltered = {
      ...filtered,
      countriesSelected: newCountriesSelected,
    };
    setFiltered(newFiltered);
  };

  const renderDropdownToggleChildren = () => (
    <>
      {!arrayUtils.isArrayEmpty(countriesSelected)
        ? countriesSelected.map((country) => (
            <Pill key={country.code}>
              {country.name}{' '}
              <img
                src={CloseSmallIcon}
                width={12}
                height={12}
                alt=""
                onClick={(e) => handleDeleteCountrySelected(e, country)}
              />
            </Pill>
          ))
        : ''}{' '}
      <ArrowDownIcon
        color="var(--ds-c-grey-dark)"
        size={{ width: 14, height: 10 }}
        styles={{
          transform: isShowDropdown && 'rotate(180deg)',
        }}
      />
    </>
  );
  const renderDropdownMenuChildren = () => (
    <>
      <DropdownSearchInput
        name={FILTER_COUNTRY_SEARCH_NAME}
        value={searchValue || ''}
        placeholder={t('form_placeholder_type_to_search')}
        onChange={handleSearch}
      />
      <DropdownMenuContainer
        style={{ borderRadius: '0 0 8px 8px', paddingBottom: 0 }}
      >
        {renderDropdownItems(
          isSearchInvalid ? [] : countriesSearch || countries
        )}
      </DropdownMenuContainer>
    </>
  );
  const renderDropdownItems = (data) => {
    if (arrayUtils.isArrayEmpty(data)) {
      return (
        <DropdownItemStyled eventKey={0} $isEmpty={isEmpty(data)}>
          {isSearchInvalid ? (
            <SearchErrorText role="alert">
              {t('form_msg_search_validation')}
            </SearchErrorText>
          ) : (
            <NoItemText>{t('form_text_no_result')}</NoItemText>
          )}
        </DropdownItemStyled>
      );
    }

    return (
      <>
        {data?.map((item, index) => (
          <DropdownItemStyled
            key={item.id}
            eventKey={index}
            onClick={(e) => handleCountrySelected(e, item)}
            style={{ display: 'flex' }}
          >
            <FormInputCheckbox
              isChecked={
                Array.isArray(countriesSelected) &&
                countriesSelected?.some(
                  (coutrySelected) => coutrySelected.code === item.code
                )
              }
            />
            {item.name}
          </DropdownItemStyled>
        ))}
      </>
    );
  };

  return (
    <CountryDropdownStyled>
      <Label>{t('label_country')}</Label>
      <DropdownCommon
        isShowDropdown={isShowDropdown}
        onToggle={handleDropdownOnToggle}
        renderDropdownToggleChildren={renderDropdownToggleChildren}
        renderDropdownMenuChildren={renderDropdownMenuChildren}
      />
      <Endpoint id={ENDPOINT_ID} />
    </CountryDropdownStyled>
  );
};

const CountryDropdownStyled = styled.div`
  margin-bottom: 32px;
  ${Fade};
`;

const NoItemText = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  display: flex;
  align-items: center;
  color: #b0b6bd;
  text-transform: initial;

  margin: 0px;
  padding: 0px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

const Pill = styled.div`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  color: #ffffff;

  padding: 4px 8px;
  min-width: fit-content;
  height: 24px;
  background: #1c3f88;
  border-radius: 100px;

  &:last-child {
    margin-right: 0px;
  }

  & img {
    margin-top: -2px;
    margin-left: 2px;
    cursor: pointer;
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 14px;
    line-height: 17px;
  }
`;
const Label = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  color: var(--ds-c-grey-dark);

  margin-bottom: 8px;
`;

const SearchErrorText = styled(NoItemText)`
  color: var(--ds-c-red);
`;

export default CustomFilterCountryDropdown;
